import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import showErrorToast from "../toast/toast";

export default function ProductWithRecipeSelectionModal({
  isOpen,
  onClose,
  onSelectProduct,
  compID,
}) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    if (!compID || !isOpen) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_RECIPES_BY_COMPANY, {
        params: { CompID: compID },
      });

      const recipesData = response.data?.data || response.data || [];
      const validProducts = Array.isArray(recipesData) ? recipesData : [];

      setProducts(validProducts);
      setFilteredProducts(validProducts);
    } catch (err) {
      setProducts([]);
      setFilteredProducts([]);
      showErrorToast("Failed to load products with recipes");
    } finally {
      setLoading(false);
    }
  }, [compID, isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    } else {
      setSearchTerm("");
      setSelectedProduct(null);
    }
  }, [isOpen, fetchProducts]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
      return;
    }
    const term = searchTerm.toLowerCase();
    const filtered = products.filter((p) => {
      const name = (p.menuItem_Name || p.menuItemName || "").toLowerCase();
      const sku = (p.menuItem_SKU || p.menuItemSku || "").toLowerCase();
      return name.includes(term) || sku.includes(term);
    });
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleSelect = () => {
    if (!selectedProduct) return;
    const id = selectedProduct.menuItem_ID ?? selectedProduct.menuItemId;
    const name = selectedProduct.menuItem_Name ?? selectedProduct.menuItemName ?? `Item #${id}`;
    onSelectProduct({ value: id, label: name, data: selectedProduct });
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Product"
      subtitle="Choose a product with recipe for this batch"
      onSubmit={handleSelect}
      showButton={true}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText="Select"
      submitButtonDisabled={!selectedProduct}
      modalSize="modal-lg"
      maxHeight="600px"
      isCentered={true}
    >
      <div>
        <div className="mb-3">
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <i
              className="fa fa-search"
              style={{
                position: "absolute",
                left: "16px",
                zIndex: 2,
                color: isDarkMode ? "#adb5bd" : "#6c757d",
                fontSize: "14px",
              }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "40px",
                backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                color: isDarkMode ? "#ffffff" : "#000000",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-5">
            <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d", margin: 0 }}>
              {products.length === 0
                ? "No products with recipes found. Create a recipe first."
                : "No products match your search."}
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {filteredProducts.map((product) => {
              const id = product.menuItem_ID ?? product.menuItemId;
              const name = product.menuItem_Name ?? product.menuItemName ?? `Item #${id}`;
              const isSelected = selectedProduct && (selectedProduct.menuItem_ID ?? selectedProduct.menuItemId) === id;

              return (
                <div
                  key={id}
                  onClick={() => handleProductClick(product)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && handleProductClick(product)}
                  style={{
                    padding: "12px 14px",
                    marginBottom: "8px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: isSelected ? `${themeColor}20` : isDarkMode ? "#1e1e1e" : "#f8f9fa",
                    border: isSelected ? `2px solid ${themeColor}` : `1px solid ${isDarkMode ? "#404040" : "#e9ecef"}`,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = isDarkMode ? "#2a2a2a" : "#e9ecef";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = isDarkMode ? "#1e1e1e" : "#f8f9fa";
                    }
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div style={{ fontWeight: "600", color: isDarkMode ? "#ffffff" : "#000000", fontSize: "14px" }}>
                        {name}
                      </div>
                      {(product.menuItem_SKU || product.menuItemSku) && (
                        <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d", fontSize: "12px" }}>
                          SKU: {product.menuItem_SKU || product.menuItemSku}
                        </small>
                      )}
                    </div>
                    {isSelected && (
                      <i className="fa fa-check-circle" style={{ color: themeColor, fontSize: "18px" }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CustomModal>
  );
}
